import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Mail, Loader2, ArrowLeft, AlertCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';

const API_URL = import.meta.env.VITE_API_URL;

export default function ValidateAccountForm() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const StateData = location.state || {};
    const [codes, setCodes] = useState(['', '', '', '', '', '']);
    const [isLoading, setIsLoading] = useState(false);
    const [isResending, setIsResending] = useState(false);
    const [timer, setTimer] = useState(30);
    const [errorMessage, setErrorMessage] = useState('');
    const [email, setEmail] = useState(
        StateData?.data?.user?.email || StateData?.data?.email || StateData?.email || ''
    );
    const [redirectPath, setRedirectPath] = useState(StateData?.redirectTo || '/app/auth/login');

    const inputRefs = useRef([]);

    console.log("Location state data: ", location.state?.data);

    useEffect(() => {
        if (!email) {
            navigate('/app/auth/register', {
                replace: true,
                state: { error: 'Invalid access. Please provide an email.' }
            });
        }
    }, [email, navigate]);

    useEffect(() => {
        let interval;
        if (isResending && timer > 0) {
            interval = setInterval(() => {
                setTimer((prev) => prev - 1);
            }, 1000);
        } else if (timer === 0) {
            setIsResending(false);
            setTimer(30);
        }
        return () => clearInterval(interval);
    }, [timer, isResending]);

    const handleCodeChange = (index, value) => {
        if (value.length <= 1 && /^\d*$/.test(value)) {
            const newCodes = [...codes];
            newCodes[index] = value;
            setCodes(newCodes);

            if (value && index < 5) {
                inputRefs.current[index + 1]?.focus();
            }
        }
    };

    const handleKeyDown = (index, e) => {
        if (e.key === 'Backspace' && !codes[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text').slice(0, 6);
        if (/^\d+$/.test(pastedData)) {
            const newCodes = [...codes];
            pastedData.split('').forEach((char, index) => {
                if (index < 6) newCodes[index] = char;
            });
            setCodes(newCodes);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setErrorMessage('');
        const code = codes.join('');

        try {
            const response = await fetch(`${API_URL}/api/auth/verify-email-code`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify({
                    code,
                    email,
                })
            });

            const data = await response.json();
            if (!response.ok || !data.success) {
                setErrorMessage(data.error || t('auth.invalidCode'));
            } else {
                navigate(redirectPath, {
                    state: {
                        verificationSuccess: true,
                        message: t('auth.accountValidated')
                    }
                });
            }
        } catch (error) {
            console.error('Error verifying code:', error);
            setErrorMessage(t('auth.serverError'));
        } finally {
            setIsLoading(false);
        }
    };

    const handleResendCode = async () => {
        if (isResending) return;
        setIsResending(true);
        setErrorMessage('');

        try {
            // Call the new resend-verification endpoint
            const response = await fetch(`${API_URL}/api/auth/resend-verification`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify({
                    email
                })
            });

            const data = await response.json();
            if (!response.ok || !data.success) {
                setErrorMessage(data.error || t('auth.resendError'));
                setIsResending(false);  // Reset resending state if there's an error
            } else {
                // Reset input fields
                setCodes(['', '', '', '', '', '']);
                // Keep timer running
            }
        } catch (error) {
            console.error('Error resending code:', error);
            setErrorMessage(t('auth.serverError'));
            setIsResending(false);  // Reset resending state if there's an error
        }
    };

    return (
        <div className="w-full max-w-md mx-auto p-6 space-y-8">
            <div className="text-center space-y-3">
                <div className="bg-blue-500/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto">
                    <Mail className="w-8 h-8 text-blue-500" />
                </div>
                <h2 className="text-2xl font-bold text-white">
                    {t('auth.validateAccount')}
                </h2>
                {email && (
                    <p className="text-gray-400 text-sm max-w-sm mx-auto">
                        {t('auth.codeSentTo')} <span className="text-white">{email}</span>
                    </p>
                )}
            </div>

            {errorMessage && (
                <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-center gap-2 p-3 bg-red-500/20 border border-red-500/50 text-red-200 rounded-lg"
                >
                    <AlertCircle className="w-5 h-5 flex-shrink-0" />
                    <span className="text-sm">{errorMessage}</span>
                </motion.div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
                <div className="flex justify-center gap-2">
                    {codes.map((code, index) => (
                        <input
                            key={index}
                            ref={(el) => (inputRefs.current[index] = el)}
                            type="text"
                            inputMode="numeric"
                            maxLength={1}
                            value={code}
                            onChange={(e) => handleCodeChange(index, e.target.value)}
                            onKeyDown={(e) => handleKeyDown(index, e)}
                            onPaste={handlePaste}
                            className="w-12 h-12 text-center text-lg font-semibold border rounded-lg 
                                     bg-white/10 border-gray-600 text-white focus:border-blue-500 
                                     focus:ring-2 focus:ring-blue-500/20 transition-all focus:outline-none"
                        />
                    ))}
                </div>

                <div className="text-center">
                    <button
                        type="button"
                        onClick={handleResendCode}
                        disabled={isResending}
                        className="text-sm text-gray-400 hover:text-white disabled:text-gray-600 
                                 disabled:cursor-not-allowed transition-colors"
                    >
                        {isResending
                            ? t('auth.resendCodeIn', { seconds: timer })
                            : t('auth.resendCode')}
                    </button>
                </div>

                <button
                    type="submit"
                    disabled={isLoading || codes.some((code) => !code)}
                    className="w-full bg-blue-500 hover:bg-blue-600 disabled:bg-blue-500/50 
                             disabled:cursor-not-allowed text-white font-semibold py-3 px-4 
                             rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                    {isLoading ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                        t('auth.validate')
                    )}
                </button>

                <div className="text-center">
                    <motion.button
                        type="button"
                        onClick={() => navigate(redirectPath)}
                        disabled={isLoading}
                        className="w-full text-gray-400 hover:text-white transition-colors 
                                 duration-200 flex items-center justify-center gap-2 text-sm"
                        whileHover={{ x: -5 }}
                    >
                        <ArrowLeft className="w-4 h-4" />
                        {t('auth.backToLogin')}
                    </motion.button>
                </div>
            </form>
        </div>
    );
}